/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, MouseEventHandler } from "react"
import { Row, Col, Checkbox, Tag } from "@duxton-web/ui"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import classnames from "classnames"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { Item } from "types/models/menu"
import { Image } from "components/Image"
import useMenuContext from "hooks/useMenuContext"
import {
  isSpecialItem,
  isItemAvailable,
  AvailableStatusesText,
  availableStatusesUsage,
  availableStatusesStyle,
  useItemWithBackTimeMessageText,
  availableStatuses,
} from "utils/item_helper"
import { OverflowTip } from "components/common"
import { SellingTimeTag } from "components/tags"
import { getSellingTimeByID } from "utils/selling_time_helper"
import { isItemSelectedV3 } from "utils/bulk_selection_helpers"
import { useSellingTimes } from "hooks/useSellingTimes"
import dayjs from "dayjs"
import styles from "./MenuItemRow.modules.css"

interface IProps {
  item: Item
  showSelection?: boolean
  className?: string
}

export const MenuItemRowV3: FC<IProps> = ({
  item,
  showSelection,
  className,
}: IProps) => {
  const dispatch = useDispatch()

  const {
    enableAlcoholSetting,
    bulkSelectionsV3,
    selectedSubDepartment,
  } = useSelector(
    (state: AppState) => ({
      enableAlcoholSetting: state.menu.enableAlcoholSetting,
      bulkSelectionsV3: state.bulkSelectionsV3,
      selectedSubDepartment: state.menu.overview.subDepartment,
    }),
    shallowEqual,
  )

  const { sellingTimes } = useSellingTimes()

  const { isServiceBasedMenuEnabled } = useMenuContext()

  // V3 mode: Calculate checked state using BulkSelectionsMetadata
  const selected = React.useMemo(() => {
    return isItemSelectedV3(
      bulkSelectionsV3,
      item?.itemID ?? "",
      item?.subDepartmentID ?? "",
      item?.departmentID ?? "",
    )
  }, [item, bulkSelectionsV3])

  const handleCheck = (value: boolean) => {
    dispatch(actionCreator.bulkSelectionsV3.multiSelectItem({ item, value }))
  }

  const isUnavailable = [
    !isItemAvailable(item),
    !enableAlcoholSetting && isSpecialItem(item),
  ].some(val => val)

  const availableUntilText = useItemWithBackTimeMessageText(
    dayjs(item.availableAt),
  )
  const availableStatusesText = AvailableStatusesText(
    availableStatusesUsage.MENU_TAG,
  )
  const renderUnavailableTag = () => {
    let tagText = ""
    if (
      item.availableStatus === availableStatuses.UNAVAILABLE_UNTIL_CUSTOM ||
      item.availableStatus === availableStatuses.UNAVAILABLE_UNTIL_NEXT_RESTOCK
    ) {
      tagText = availableUntilText
    } else {
      tagText = availableStatusesText[item.availableStatus as number]
    }

    return (
      isUnavailable &&
      tagText && (
        <Col>
          <Tag
            size="xsmall"
            color={
              availableStatusesStyle[availableStatusesUsage.MENU_TAG][
                item.availableStatus as number
              ] ?? ""
            }
          >
            {tagText}
          </Tag>
        </Col>
      )
    )
  }

  const handleClickSelected: MouseEventHandler<HTMLElement> = e => {
    e.stopPropagation()
    if (!showSelection) {
      dispatch(
        actionCreator.menu.toggleDrawer({
          showItemSelection: true,
        }),
      )
    }
  }

  return (
    <Row
      wrap={false}
      gutter={16}
      className={classnames(styles.rowStyle, className)}
    >
      <Col>
        <Image
          src={item.imageURL}
          width={73}
          height={73}
          opacity={isUnavailable ? 0.5 : 1}
        />
      </Col>
      <Col flex="auto">
        <OverflowTip
          className={classnames("name", isUnavailable && "unavailable")}
        >
          {item.itemName || ""}
        </OverflowTip>
        <OverflowTip
          className={classnames("price", isUnavailable && "unavailable")}
        >
          {isServiceBasedMenuEnabled ? item.priceRange : item.priceDisplay}
        </OverflowTip>
      </Col>
      {renderUnavailableTag()}
      {item.sellingTimeID !== selectedSubDepartment?.sellingTimeID && (
        <Col>
          <SellingTimeTag
            sellingTime={getSellingTimeByID(item.sellingTimeID, sellingTimes)}
          />
        </Col>
      )}

      <Col className={`checkbox-container ${showSelection ? "show" : ""}`}>
        <Checkbox
          defaultChecked={false}
          checked={selected}
          onChange={(e: any) => {
            handleCheck(e.target.checked)
          }}
          onClick={handleClickSelected}
        />
      </Col>
    </Row>
  )
}
